import { useState, useEffect } from 'react';
import { invoicesApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Building2, Landmark, Mail, CreditCard, CheckCircle2, AlertTriangle, BellRing, Send } from 'lucide-react';
import { XeroPanel } from './XeroPanel';

const FIELDS = {
  business: [['business_name', 'Trading name', true], ['abn', 'ABN'], ['address', 'Address'], ['phone', 'Phone'], ['email', 'Contact email (reply-to)']],
  bank: [['bank_name', 'Bank'], ['account_name', 'Account name'], ['bsb', 'BSB'], ['account_number', 'Account number']],
};

function StatusRow({ ok, icon: Icon, label, detail, testId }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-md border ${ok ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid={testId}>
      <Icon className="h-5 w-5 mt-0.5 shrink-0" />
      <div className="text-sm"><p className="font-medium flex items-center gap-1.5">{label} {ok ? <CheckCircle2 className="h-4 w-4 text-green-700" /> : <AlertTriangle className="h-4 w-4 text-amber-700" />}</p><p className="text-xs text-muted-foreground">{detail}</p></div>
    </div>
  );
}

export function InvoiceSettingsPanel() {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    const to = form.alert_email || status.from_address;
    setTesting(true);
    try { const { data } = await invoicesApi.testEmail(to); toast.success(`Test email sent to ${data.to} — check the inbox (and spam)`); }
    catch (err) { toast.error(err.response?.data?.detail || 'Test email failed', { duration: 15000 }); }
    finally { setTesting(false); }
  };

  useEffect(() => {
    let cancelled = false;
    invoicesApi.getSettings().then(({ data }) => {
      if (cancelled) return;
      const { email_configured, from_address, stripe_ready, ...rest } = data;
      setForm(rest); setStatus({ email_configured, from_address, stripe_ready });
    }).catch(() => { if (!cancelled) toast.error('Failed to load invoice settings'); });
    return () => { cancelled = true; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await invoicesApi.updateSettings(form); toast.success('Invoice settings saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="animate-pulse h-64 bg-muted rounded-md" />;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="invoice-settings-panel">
      <div className="grid md:grid-cols-2 gap-3">
        <StatusRow ok={status.email_configured} icon={Mail} label="Invoice emails" testId="email-status"
          detail={status.email_configured ? `Sending from ${status.from_address}. Use "Send test email" below to check it works.` : `Not set up yet — invoices can still be downloaded as PDF. Ask your developer to add the SMTP login for ${status.from_address || 'your email'}.`} />
        <StatusRow ok={status.stripe_ready} icon={CreditCard} label="Pay now by card (Stripe)" testId="stripe-status"
          detail={status.stripe_ready ? 'Customers can pay the invoice online from their order page.' : 'Stripe is not configured.'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Business details on the invoice</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {FIELDS.business.map(([k, label, req]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label><Input required={req} value={form[k] || ''} onChange={set(k)} data-testid={`setting-${k}`} /></div>
            ))}
            <label className="flex items-center gap-2 text-sm pt-1"><Switch checked={form.gst_registered} onCheckedChange={(v) => setForm((f) => ({ ...f, gst_registered: v }))} data-testid="setting-gst" /> GST registered (shows "Tax Invoice" and GST amount)</label>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> Bank transfer details</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {FIELDS.bank.map(([k, label]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label><Input value={form[k] || ''} onChange={set(k)} data-testid={`setting-${k}`} /></div>
            ))}
            <div className="space-y-1.5"><Label>Payment note (printed on invoice & email)</Label><Textarea rows={3} value={form.payment_note || ''} onChange={set('payment_note')} data-testid="setting-payment_note" /></div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><BellRing className="h-4 w-4 text-primary" /> Order emails</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.send_staff_alert ?? true} onCheckedChange={(v) => setForm((f) => ({ ...f, send_staff_alert: v }))} data-testid="setting-send_staff_alert" /> Email me when a website order comes in</label>
            <div className="space-y-1.5"><Label>Send order alerts to</Label><Input type="email" value={form.alert_email || ''} onChange={set('alert_email')} placeholder="terri@..." data-testid="setting-alert_email" /></div>
            <Button type="button" size="sm" variant="outline" onClick={sendTest} disabled={testing || !status.email_configured} data-testid="send-test-email"><Send className="h-4 w-4 mr-1" /> {testing ? 'Sending…' : 'Send test email'}</Button>
          </div>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.send_customer_confirmation ?? true} onCheckedChange={(v) => setForm((f) => ({ ...f, send_customer_confirmation: v }))} data-testid="setting-send_customer_confirmation" /> Send customers an order confirmation email</label>
            <p className="text-xs text-muted-foreground">Includes their order number, items, totals, the booked or preferred date and a link to track the order. {status.email_configured ? '' : 'Emails start sending once email is set up.'}</p>
          </div>
        </CardContent>
      </Card>

      <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-invoice-settings">{saving ? 'Saving…' : 'Save invoice settings'}</Button>

      <XeroPanel />
    </form>
  );
}
